import { useState, useEffect } from 'react';
import { Flex, Button, Modal, Tooltip } from 'antd';
import AddStatus from './addStatus';
import EditStatus from './editStatus';
import StatusesList from './statusesList';
import { SubTitle, getStatusPosition, Skeletons } from './helper';
import { useLeadService } from '../../services/leadService';
import { useTranslation } from 'react-i18next';
import { useMedia } from '../../hooks/useMediaQuery';
import globalNotification from '../../common/hooks/globalNotification';
import { notificationType } from '../../common/constants/notificationType';
import { spacing, buttonStyle } from '../../theming';
import { DndContext, PointerSensor, TouchSensor, closestCorners, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import classes from './style.module.scss';
import icon from '../../common/styles/Icon.module.scss';
import AddButton from '../../common/components/AddButton';
import { AnimatePresence, motion } from 'framer-motion';
import { statusesForm } from '../../common/animationVariants/statusesForm';

export default function Statuses() {
    const [statuses, setStatuses] = useState([]);
    const [mode, setMode] = useState(null);
    const [colors, setColors] = useState([]);
    const [loading, setLoading] = useState({
        create: false,
        update: false,
        delete: false,
        fetch: false,
    });

    const { getStatuses, deleteStatus, createStatus, updateStatus, updateStatusOrder } = useLeadService();
    const { t } = useTranslation(['settings', 'apiResponses', 'form']);
    const sensors = useSensors(useSensor(PointerSensor), useSensor(TouchSensor));

    const { isSmallDevice } = useMedia();

    // #region fetchStatuses
    async function fetchStatuses() {
        setLoading(prev => ({ ...prev, fetch: true }));
        try {
            const res = await getStatuses();
            setStatuses(res.data.statuses);
            setColors(res.data.colors);
        } catch (err) {
            console.log(err); // just to see the error. all other axios will handle
        } finally {
            setLoading(prev => ({ ...prev, fetch: false }));
        }
    }

    useEffect(() => {
        fetchStatuses();
    }, []);
    // #endregion

    // #region handlers
    const handleCreateStatus = async statusList => {
        setLoading(prev => ({ ...prev, create: true }));
        try {
            for (const status of statusList) {
                await createStatus({
                    name: status.name,
                    primaryColor: status.primaryColor,
                });
            }

            globalNotification.open({
                type: notificationType.SUCCESS,
                message: t('statuses.notification.created'),
                placement: 'top',
            });

            fetchStatuses();
        } catch (err) {
            const errCode = err?.response?.data?.code;

            globalNotification.open({
                type: 'error',
                message: errCode ? t(`apiResponses:${errCode}.message`) : err?.response.data.message,
                description: errCode ? t(`apiResponses:${errCode}.description`) : err?.response.data.description,
                placement: 'top',
            });
        } finally {
            setLoading(prev => ({ ...prev, create: false }));
            setMode(null);
        }
    };

    const handleUpdateStatus = async statusesToChange => {
        setLoading(prev => ({ ...prev, update: true }));
        try {
            for (const status of statusesToChange) {
                await updateStatus(status.id, {
                    name: status.name,
                    primaryColor: status.primaryColor,
                });
            }

            globalNotification.open({
                type: notificationType.SUCCESS,
                message: t('statuses.notification.updated'),
                placement: 'top',
            });

            fetchStatuses();
        } catch (err) {
            const errCode = err?.response?.data?.code;

            globalNotification.open({
                type: notificationType.ERROR,
                message: errCode ? t(`apiResponses:${errCode}.message`) : err?.response.data.message,
                description: errCode ? t(`apiResponses:${errCode}.description`) : err?.response.data.description,
                placement: 'top',
            });
        } finally {
            setLoading(prev => ({ ...prev, update: false }));
            setMode(null);
        }
    };

    const handleOpenDeleteModal = id => {
        Modal.confirm({
            danger: true,
            title: t('statuses.modal.title'),
            onOk: () => {
                handleDeleteStatus(id);
            },
            okText: t('statuses.modal.ok'),
            okType: 'danger',
            okButtonProps: {
                type: 'primary',
            },
            cancelText: t('statuses.modal.cancel'),
            className: classes['delete-modal'],
            centered: true,
        });
    };

    const handleDeleteStatus = async id => {
        setLoading(prev => ({ ...prev, delete: id }));
        try {
            await deleteStatus(id);

            globalNotification.open({
                type: notificationType.SUCCESS,
                message: t('statuses.notification.deleted'),
                placement: 'top',
            });

            fetchStatuses();
        } catch (err) {
            const errCode = err?.response?.data?.code;

            globalNotification.open({
                type: notificationType.ERROR,
                message: t(`apiResponses:${errCode}.message`),
                description: t(`apiResponses:${errCode}.description`),
                placement: 'top',
            });
        } finally {
            setLoading(prev => ({ ...prev, delete: false }));
        }
    };

    const handleDragEnd = async event => {
        const { active, over } = event;

        if (active.id === over.id) {
            return;
        }

        const activeIndex = getStatusPosition(active.id, statuses);
        const overIndex = getStatusPosition(over.id, statuses);

        if (activeIndex !== overIndex) {
            const newStatuses = arrayMove(statuses, activeIndex, overIndex);

            const updatedOrder = newStatuses.map((status, index) => ({
                ...status,
                statusOrder: index + 1,
            }));

            try {
                await updateStatusOrder(updatedOrder);
                setStatuses(updatedOrder);
            } catch (error) {
                console.error('Failed to update status order:', error);
            }
        }
    };
    // #endregion

    return (
        <Flex vertical gap={isSmallDevice ? spacing[1] : spacing[3]}>
            <Flex justify="space-between" align="center" gap={isSmallDevice ? 0 : spacing[3]}>
                <Flex gap={spacing[3]} align="center">
                    <div className={classes['status-icon']}>
                        <i className="fi-rr-ballot" />
                    </div>
                    <Flex vertical>
                        <Flex
                            align="center"
                            gap={isSmallDevice ? 0 : spacing[2]}
                            style={{
                                width: 'fit-content',
                            }}
                        >
                            <h5 className={classes.title}>{t('statuses.title')}</h5>
                            <Button
                                onClick={() => {
                                    setMode(prevMode => (prevMode === 'edit' ? null : 'edit'));
                                }}
                                type="link"
                                className={classes['edit-button']}
                            >
                                <i className="fi-rr-edit" style={{ marginTop: spacing[0.5] }} />
                            </Button>
                        </Flex>
                        {!isSmallDevice && <SubTitle classes={classes} t={t} />}
                    </Flex>
                </Flex>
                <Tooltip title={statuses.length === 12 ? t('statuses.tooltip.maxLimitReached') : ''}>
                    <AddButton
                        style={{
                            ...(isSmallDevice ? buttonStyle.medium : buttonStyle.large),
                        }}
                        iconSize={icon.sm}
                        text={t('statuses.button')}
                        onClick={() => {
                            setMode(prevMode => (prevMode === 'add' ? null : 'add'));
                        }}
                        disabled={statuses.length === 12}
                    />
                </Tooltip>
            </Flex>
            {isSmallDevice && <SubTitle classes={classes} t={t} />}
            {loading.fetch ? (
                <Skeletons />
            ) : (
                mode !== 'edit' && (
                    <motion.div key="edit" {...statusesForm}>
                        <DndContext sensors={sensors} collisionDetection={closestCorners} onDragEnd={handleDragEnd}>
                            <StatusesList statuses={statuses} />
                        </DndContext>
                    </motion.div>
                )
            )}
            <AnimatePresence mode="wait">
                {mode === 'edit' && (
                    <motion.div key="edit" {...statusesForm}>
                        <EditStatus
                            t={t}
                            statuses={statuses}
                            colors={colors}
                            handleUpdateStatus={handleUpdateStatus}
                            handleOpenDeleteModal={handleOpenDeleteModal}
                            loading={loading}
                        />
                    </motion.div>
                )}
                {mode === 'add' && (
                    <motion.div key="add" {...statusesForm}>
                        <AddStatus t={t} colors={colors} handleCreateStatus={handleCreateStatus} loading={loading} />
                    </motion.div>
                )}
            </AnimatePresence>
        </Flex>
    );
}
